import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, Mail, Globe, ChevronDown, Search, Download, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import ProductsMegaMenu from "@/components/layout/ProductsMegaMenu";
import IndustriesMegaMenu from "@/components/layout/IndustriesMegaMenu";
import ResourcesMegaMenu from "@/components/layout/ResourcesMegaMenu";
import CompanyMegaMenu from "@/components/layout/CompanyMegaMenu";
import MobileNavAccordion from "@/components/layout/MobileNavAccordion";
import { visualForIndustry } from "@/data/industryVisualData";
import { visualForCompanyCard } from "@/data/companyMenuVisualData";
import { visualForProductCategory } from "@/data/productMenuVisualData";
import { toAbsoluteUrl } from "@/lib/imageUrl";
import { useSettings } from "@/contexts/SettingsContext";
import BrandLogo from "@/components/common/BrandLogo";

// Fallback used only if /menus/header_main hasn't loaded yet (or is empty) —
// keeps the header from going blank on a transient API failure.
const NAV = [
  { label: "Products", to: "/products", key: "products" },
  { label: "Industries", to: "/industries", key: "industries" },
  { label: "Services", to: "/services", key: "services" },
  { label: "Resources", to: "/resources", key: "resources" },
  { label: "Company", to: "/company/about", key: "company" },
  { label: "Contact", to: "/contact", key: "contact" },
];

// The mega-menu dropdowns are keyed to specific site sections (products,
// industries, resources, company); a nav item's "key" is derived from its
// URL's first path segment so admin-added items automatically get the right
// dropdown when they point at one of those sections, and just render as a
// plain link (no dropdown) otherwise.
function navKeyFromUrl(url) {
  const clean = (url || "/").split("?")[0].split("#")[0];
  const segment = clean.replace(/^\/+/, "").split("/")[0];
  return segment || "home";
}

export default function Header() {
  const [menu, setMenu] = useState({ products: [], industries: [] });
  const [megaMenu, setMegaMenu] = useState(null);
  const [industriesFull, setIndustriesFull] = useState([]);
  const [navItems, setNavItems] = useState([]);
  const [openKey, setOpenKey] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const navigate = useNavigate();
  // On the catalogue library itself, "Download Catalogue" must not reload the page it is on — it jumps to the library instead.
  const onCatalogues = useLocation().pathname.replace(/\/$/, "") === "/resources/catalogues";
  const toLibrary = (e, after) => { e.preventDefault(); after?.(); document.getElementById("catalogue-library")?.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" }); };
  const { settings } = useSettings();

  function onSearchSubmit(e) {
    e.preventDefault();
    if (searchQ.trim().length < 2) return;
    navigate(`/search?q=${encodeURIComponent(searchQ.trim())}`);
    setSearchOpen(false);
    setSearchQ("");
  }

  useEffect(() => {
    api.get("/menu").then((r) => setMenu(r.data)).catch(() => {});
    api.get("/megamenu").then((r) => setMegaMenu(r.data)).catch(() => {});
    api.get("/industries").then((r) => setIndustriesFull(r.data)).catch(() => {});
    api.get("/menus/header_main").then((r) => setNavItems(r.data?.items || [])).catch(() => {});
  }, []);

  const nav = (navItems.length ? navItems : NAV.map((n) => ({ label: n.label, url: n.to })))
    .slice()
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((item) => ({ label: item.label, to: item.url || item.to || "/", key: navKeyFromUrl(item.url || item.to) }));

  // Mobile accordions (phones/tablets never get the desktop mega menus): Products, Industries and Company expand into thumbnail lists.
  const mobileSubmenus = {
    products: (megaMenu?.categories || []).filter((c) => c.slug).map((c) => {
      // Admin → Mega Menu → Category Image wins when set (same rule as the desktop mega menu's tiles); the
      // pre-cropped cutout below is only a fallback for a category that has never had an image set in Admin.
      if (c.image) return { href: `/products/${c.slug}`, label: c.title, thumb: toAbsoluteUrl(c.image), tile: false };
      const v = visualForProductCategory(c.slug);
      return { href: `/products/${c.slug}`, label: c.title, thumb: v?.thumb.src, thumbW: v?.thumb.width, thumbH: v?.thumb.height, tile: !!v };
    }),
    industries: [...industriesFull].sort((a, b) => (a.order || 0) - (b.order || 0)).map((ind) => {
      // Same rule as the Products thumbs above: a static entry's pre-cropped thumb wins when one exists; otherwise
      // fall back to the industry's own photo (Admin → Industries → hero_image/image) rather than showing nothing.
      const v = visualForIndustry(ind.slug);
      const dbImage = ind.hero_image || ind.image;
      return { href: `/industries/${ind.slug}`, label: ind.name, thumb: v?.thumb.src || (dbImage ? toAbsoluteUrl(dbImage) : undefined) };
    }),
    // Company: the card image set in Admin → Settings → Company Menu wins (same as the desktop mega menu); the curated
    // pre-cropped thumb is used only while the card still points at its curated default image.
    company: (settings?.company_menu_sections || []).filter((c) => c.url).map((c) => {
      const v = visualForCompanyCard(c.url);
      return { href: c.url, label: c.title, thumb: c.image && c.image !== v?.image ? toAbsoluteUrl(c.image) : v?.thumb };
    }),
  };

  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setSearchOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  return (
    <header className="sticky top-0 z-50 font-inter" role="banner">
      {/* Top bar */}
      <div style={{ backgroundColor: "#071B3A" }} className="h-9 text-white/80 flex items-center overflow-hidden">
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 flex items-center justify-between w-full text-xs font-medium">
          <div className="hidden md:flex items-center gap-6">
            {/* <a href="tel:+919594945572" className="flex items-center gap-2 hover:text-blue-300 transition-colors" data-testid="topbar-phone">
              <Phone className="w-4 h-4" /> +91 9594945572
            </a>
            <a href="mailto:enquiry@shingare.com" className="flex items-center gap-2 hover:text-blue-300 transition-colors" data-testid="topbar-email">
              <Mail className="w-4 h-4" /> enquiry@shingare.com
            </a> */}
            <a
  href={`tel:${settings?.phone || settings?.mobile || ""}`}
  className="flex items-center gap-1.5 hover:text-blue-300 transition-colors"
>
  <Phone className="w-3.5 h-3.5" />
  {settings?.phone || settings?.mobile}
</a>

<a
  href={`mailto:${settings?.email || ""}`}
  className="flex items-center gap-1.5 hover:text-blue-300 transition-colors"
>
  <Mail className="w-3.5 h-3.5" />
  {settings?.email}
</a>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-1.5 hover:text-blue-300 transition-colors"><Globe className="w-3.5 h-3.5" /> Exporting to 45+ Countries</span>
            <span className="hidden md:block w-px h-3 bg-white/20" />
            <span className="hidden md:inline hover:text-blue-300 transition-colors">ISO 9001:2015 Certified</span>
            <span className="hidden lg:block w-px h-3 bg-white/20" />
            <span className="hidden lg:flex items-center gap-1.5 hover:text-blue-300 transition-colors">
              <svg width="14" height="10" viewBox="0 0 18 12" role="img" aria-label="India flag" className="shrink-0 rounded-[1px] shadow-[0_0_0_1px_rgba(255,255,255,0.15)]">
                <rect width="18" height="4" y="0" fill="#FF9933" />
                <rect width="18" height="4" y="4" fill="#FFFFFF" />
                <rect width="18" height="4" y="8" fill="#138808" />
                <circle cx="9" cy="6" r="1.5" fill="none" stroke="#000088" strokeWidth="0.3" />
                <circle cx="9" cy="6" r="0.3" fill="#000088" />
              </svg>
              Made in India
            </span>
          </div>
        </div>
      </div>

      {/* Main nav — a single shared onMouseLeave here (rather than on each
          trigger and each mega menu separately) is what keeps a mega menu
          open while the cursor crosses the gap between the nav link and the
          dropdown panel below it: both are DOM descendants of this div, so
          "leaving" only fires once the cursor exits this whole region. */}
      <div
        className="bg-white/[0.96] border-b border-gray-100 shadow-sm"
        onMouseLeave={() => setOpenKey(null)}
      >
        <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 grid grid-cols-[auto_1fr_auto] items-center gap-6 h-[76px]">
          <Link to="/" className="flex items-center shrink-0" data-testid="header-logo">
            <BrandLogo className="h-auto w-[120px] sm:w-[132px] lg:w-[150px]" />
          </Link>

          <nav className="hidden lg:flex items-center justify-center gap-5 xl:gap-6 2xl:gap-9" aria-label="Primary">
            {nav.map((n) => (
              <div
                key={n.key}
                className="relative"
                onMouseEnter={() => setOpenKey(n.key)}
              >
                <NavLink
                  to={n.to}
                  end
                  data-testid={`nav-${n.key}`}
                  className={({ isActive }) =>
                    `group relative py-2 text-[15px] font-medium tracking-[0.1px] flex items-center gap-1 whitespace-nowrap transition-colors ${
                      isActive ? "text-[#2563EB]" : "text-[#14213D] hover:text-[#2563EB]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {n.label}
                      {["products", "industries", "resources", "company"].includes(n.key) && (
                        <ChevronDown
                          className={`w-3.5 h-3.5 transition-transform duration-300 ${openKey === n.key ? "rotate-180" : ""}`}
                        />
                      )}
                      <span
                        className={`absolute left-0 -bottom-1 h-[2px] rounded-full bg-[#2563EB] transition-all duration-300 ${
                          isActive ? "w-full" : "w-0 group-hover:w-full"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              </div>
            ))}
          </nav>

          <div className="flex items-center justify-end gap-3">
            <div className="hidden md:flex items-center gap-3">
              <button
                onClick={() => setSearchOpen(true)}
                aria-label="Open site search"
                className="w-11 h-11 flex items-center justify-center rounded-xl border border-[#E5E7EB] text-[#14213D] hover:bg-[#EFF6FF] hover:text-[#2563EB] hover:border-[#BFDBFE] transition-all duration-200"
                data-testid="header-search"
              >
                <Search className="w-[18px] h-[18px]" />
              </button>
              <Link
                to="/resources/catalogues"
                onClick={onCatalogues ? toLibrary : undefined}
                className="hidden xl:inline-flex items-center justify-center gap-2.5 h-11 px-5 whitespace-nowrap rounded-xl border border-[#E5E7EB] bg-white text-[#0B2148] text-sm font-semibold hover:bg-[#EFF6FF] hover:text-[#2563EB] hover:border-[#BFDBFE] transition-all duration-200"
                data-testid="header-download-catalogue"
              >
                <Download className="w-4 h-4" />
                Download Catalogue
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 h-11 px-6 whitespace-nowrap rounded-xl bg-[#2563EB] text-white text-sm font-semibold shadow-[0_8px_20px_-8px_rgba(37,99,235,0.6)] hover:bg-[#1D4ED8] hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-8px_rgba(37,99,235,0.7)] transition-all duration-200"
                data-testid="header-request-quote"
              >
                Request a Quote
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <button className="lg:hidden p-2 text-[#1A2F4A]" onClick={() => setMobileOpen((s) => !s)} aria-label={mobileOpen ? "Close menu" : "Open menu"} aria-expanded={mobileOpen} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          // Scrolls inside itself: the header is sticky, so a tall (expanded) list would otherwise be cut off below the fold.
          <div className="lg:hidden max-h-[calc(100dvh-7rem)] overflow-y-auto overscroll-contain border-t border-gray-100 bg-white">
            <div className="container-page py-4 flex flex-col gap-1">
              {nav.map((n) => {
                const sub = mobileSubmenus[n.key];
                return sub && sub.length ? (
                  <MobileNavAccordion
                    key={n.key}
                    id={n.key}
                    label={n.label}
                    to={n.to}
                    allLabel={n.key === "products" ? "All products" : "All industries"}
                    showAll={n.key === "industries" || n.key === "products"}
                    items={sub}
                    open={mobileSection === n.key}
                    onToggle={() => setMobileSection((cur) => (cur === n.key ? null : n.key))}
                    onNavigate={() => { setMobileOpen(false); setMobileSection(null); }}
                  />
                ) : (
                  <Link
                    key={n.key}
                    to={n.to}
                    onClick={() => setMobileOpen(false)}
                    className="py-3 text-[15px] font-semibold tracking-wide text-[#1A2F4A] hover:text-[#003F8E] border-b border-gray-100"
                    data-testid={`mobile-nav-${n.key}`}
                  >
                    {n.label}
                  </Link>
                );
              })}
              <div className="flex gap-3 pt-4">
                <Link to="/resources/catalogues" onClick={onCatalogues ? (e) => toLibrary(e, () => setMobileOpen(false)) : undefined} className="flex-1 inline-flex items-center justify-center h-11 rounded-full border border-blue-200 bg-white text-[#0B1F44] text-sm font-semibold text-center">Catalogue</Link>
                <Link to="/contact" className="flex-1 inline-flex items-center justify-center gap-1.5 h-11 rounded-full bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-semibold text-center shadow-lg">Request Quote <ArrowRight className="w-3.5 h-3.5" /></Link>
              </div>
            </div>
          </div>
        )}

        {/* Full-width Products mega menu — sibling of nav bar so it spans entire header width.
            No per-panel hover handlers here: the shared onMouseLeave on the
            outer nav wrapper above is what keeps this open/closes it. */}
        {openKey === "products" && <ProductsMegaMenu data={megaMenu} onClose={() => setOpenKey(null)} />}

        {/* Full-width Industries mega menu */}
        {openKey === "industries" && (
          <IndustriesMegaMenu
            industries={industriesFull}
            stats={settings?.industries_stats || []}
            onClose={() => setOpenKey(null)}
          />
        )}

        {/* Full-width Resources mega menu */}
        {openKey === "resources" && (
          <ResourcesMegaMenu
            cards={settings?.resources_menu_cards || []}
            cta={{
              headline: settings?.resources_menu_cta_headline,
              text: settings?.resources_menu_cta_text,
              label: settings?.resources_menu_cta_label,
              url: settings?.resources_menu_cta_url,
            }}
            onClose={() => setOpenKey(null)}
          />
        )}

        {/* Full-width Company mega menu */}
        {openKey === "company" && (
          <CompanyMegaMenu
            sections={settings?.company_menu_sections || []}
            cta={{
              headline: settings?.company_menu_cta_headline,
              text: settings?.company_menu_cta_text,
              label: settings?.company_menu_cta_label,
              url: settings?.company_menu_cta_url,
            }}
            onClose={() => setOpenKey(null)}
          />
        )}
      </div>

      {searchOpen && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center pt-24 px-4" onClick={() => setSearchOpen(false)} role="dialog" aria-modal="true" aria-label="Search">
          <form onSubmit={onSearchSubmit} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white rounded-lg shadow-2xl">
            <div className="relative">
              <Search className="w-5 h-5 text-[#6C757D] absolute left-5 top-1/2 -translate-y-1/2" />
              <input autoFocus value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search products, industries…" data-testid="header-search-input" className="w-full pl-14 pr-6 py-5 text-lg outline-none" />
              <button type="button" onClick={() => setSearchOpen(false)} aria-label="Close search" className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-[#6C757D] hover:text-[#1A2F4A]"><X className="w-4 h-4" /></button>
            </div>
            <div className="px-6 py-3 text-xs text-[#6C757D] border-t border-gray-100 flex items-center justify-between">
              <span>Press <kbd className="px-1.5 py-0.5 border border-gray-200 rounded text-[10px]">Enter</kbd> to search all content</span>
              <button type="submit" className="bg-[#003F8E] text-white px-4 py-2 text-xs font-heading font-bold uppercase tracking-widest">Search</button>
            </div>
          </form>
        </div>
      )}
    </header>
  );
}

function MegaMenu({ children, width = "w-[840px]" }) {
  return (
    <div className={`absolute top-full left-1/2 -translate-x-1/2 ${width} bg-white shadow-2xl border border-gray-100 rounded-b-lg`}>
      {children}
    </div>
  );
}

function MegaFooter({ to, label }) {
  return (
    <div className="border-t border-gray-100 bg-[#F8F9FA] px-8 py-3">
      <Link to={to} className="text-xs font-bold uppercase tracking-widest text-[#003F8E] flex items-center gap-2">
        {label} <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}
